import {
  BackButtonCWUI,
  CustomerCategoryTitleUI,
  SkeletonCustomerCategoryTitleUI,
} from '@next-order/next-order-components';
import { BREAKPOINT_VALUES, ORDER_STAGE_TYPES } from 'helpers/enums';
import { ReactComponent as PersonalIcon } from '../../assets/images/person.circle.svg';
import { ReactComponent as AddressIcon } from '../../assets/images/house.svg';
import { ReactComponent as PaymentIcon } from '../../assets/images/creditcard.svg';
import { ReactComponent as OrderIcon } from '../../assets/images/bag.svg';
import { ReactComponent as LogoutIcon } from '../../assets/images/door.left.hand.open.svg';
import { ReactComponent as DeleteAccountIcon } from '../../assets/images/trash.icon.svg';
import { ReactComponent as RightArrowIcon } from '../../assets/images/r-arrow.svg';
import AnimationWrapper from 'components/AnimationWrapper';
import ConfirmationModal from './ConfirmationModal';
import PersonalInformation from './PersonalInformation';
import Orders from './Orders';
import { SavedAddresses } from './Address';
import { useState } from 'react';
import {
  setState as setPropsState,
  toggleMobileCart,
} from 'containers/Home/actions';
import { useDispatch, useSelector } from 'react-redux';
import { defaultHomePropsState } from 'constants/common';
import './index.css';
import PaymentMethods from './PaymentMethods';

const onClickDefaultState = {
  loyaltyOpen: false,
  aboutOpen: false,
  bookingOpen: false,
  profileOpen: false,
};

export default function Profile({
  breakPoint,
  userDetails,
  currentUser,
  sidebarAnimationClass,
  signOut,
  deleteAccount,
  fetchCreditCardsAdyen,
}) {
  const dispatch = useDispatch();
  const currentOrder = useSelector((state) => state.homeReducer.currentOrder);
  const [confirmAction, setConfirmAction] = useState(null);
  const [showPersonalInfo, setShowPersonalInfo] = useState(false);
  const [showOrders, setShowOrders] = useState(false);
  const [showPaymentMethods, setShowPaymentMethods] = useState(false);
  const [showSavedAddresses, setShowSavedAddresses] = useState(false);

  const showDeleteAccountButton =
    typeof deleteAccount === 'function' &&
    (currentUser?.phoneNumber || userDetails?.mobileNumber) &&
    userDetails?.firstName &&
    currentOrder?.stage < ORDER_STAGE_TYPES.REVIEW;

  let formattedDate = '';

  if (userDetails?.date) {
    formattedDate = new Date(userDetails.date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } else if (userDetails?.dateTimestamp) {
    let timestamp = userDetails.dateTimestamp;

    // Check if it's in seconds (10-digit) and convert to milliseconds
    if (timestamp < 1e12) {
      timestamp *= 1000;
    }

    formattedDate = new Date(timestamp).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } else {
    // Fallback to today's date
    formattedDate = new Date().toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  }

  // Don't render if userDetails hasn't loaded yet
  if (!userDetails) {
    return (
      <AnimationWrapper
        animation='fade-in'
        style={{
          width: '100%',
          height: '100%',
        }}
        className='profile-section'
      >
        <aside className='sidebar'>
          <div className='sidebar__wrapper'>
            <div className='card__about'>
              <BackButtonCWUI
                extendedClassName={`mt-4 ml-5 ${
                  breakPoint <= BREAKPOINT_VALUES.DESKTOP ? 'hidden' : ''
                }`}
                onClick={() => {}}
              />
              <div
                className={`profile-header ${
                  breakPoint <= BREAKPOINT_VALUES.DESKTOP ? 'pt-[50px]' : ''
                }`}
              >
                <SkeletonCustomerCategoryTitleUI
                  extendedClassName={`${
                    breakPoint >= BREAKPOINT_VALUES.DESKTOP ? 'mt-4' : ''
                  }`}
                />
              </div>

              <div className='user-details'>
                {/* User name skeleton */}
                <SkeletonCustomerCategoryTitleUI extendedClassName='!mb-5 !h-8 !w-48' />
                <div className='flex mt-5 gap-30'>
                  <div>
                    <SkeletonCustomerCategoryTitleUI extendedClassName='!mb-2 !h-4 !w-24' />
                    <SkeletonCustomerCategoryTitleUI extendedClassName='!h-6 !w-20' />
                  </div>
                  <div>
                    <SkeletonCustomerCategoryTitleUI extendedClassName='!mb-2 !h-4 !w-28' />
                    <SkeletonCustomerCategoryTitleUI extendedClassName='!h-6 !w-16' />
                  </div>
                </div>
              </div>

              {/* Profile items skeleton */}
              <div className='mt-3 max-h-[calc(100vh-300px)] overflow-y-auto pr-2'>
                {[...Array(5)].map((_, index) => (
                  <div
                    key={`profile-skeleton-${index}`}
                    className='flex items-center justify-between px-6 py-[22px]'
                  >
                    <div className='flex items-center gap-4'>
                      <div className='w-7 h-7 bg-gray-200 rounded-full animate-pulse' />
                      <SkeletonCustomerCategoryTitleUI extendedClassName='!h-6 !w-40' />
                    </div>
                    {index < 4 && (
                      <div className='w-4 h-4 bg-gray-200 rounded animate-pulse' />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </aside>
      </AnimationWrapper>
    );
  }

  const profileItems = [
    {
      name: 'Personal Information',
      icon: PersonalIcon,
      onClick: () => {
        setShowPersonalInfo(true);
      },
    },
    {
      name: 'Saved Addresses',
      icon: AddressIcon,
      onClick: () => {
        setShowSavedAddresses(true);
      },
    },
    {
      name: 'Payment Methods',
      icon: PaymentIcon,
      onClick: () => {
        setShowPaymentMethods(true);
      },
    },
    {
      name: 'Order History',
      icon: OrderIcon,
      onClick: () => {
        setShowOrders(true);
      },
    },
    {
      name: 'Log Out',
      icon: LogoutIcon,
      isNotShowArrow: true,
      onClick: () => {
        setConfirmAction('logout');
      },
    },
    ...(showDeleteAccountButton
      ? [
          {
            name: 'Delete Account',
            icon: DeleteAccountIcon,
            isNotShowArrow: true,
            isDestructive: true,
            onClick: () => {
              setConfirmAction('delete');
            },
          },
        ]
      : []),
  ];

  const logOutConfirmHandler = () => {
    dispatch(toggleMobileCart(false));
    dispatch(setPropsState({ ...onClickDefaultState }));
    signOut();
  };

  const confirmModalConfig = {
    logout: {
      title: 'Are you sure you want to log out?',
      confirmText: 'Log Out',
      destructive: false,
      onConfirm: logOutConfirmHandler,
    },
    delete: {
      title: 'Are you sure you want to delete your account?',
      description:
        'This action is permanent. All your data will be removed and cannot be recovered.',
      confirmText: 'Delete Account',
      destructive: true,
      onConfirm: () => deleteAccount?.(),
    },
  };

  const activeConfirm = confirmAction ? confirmModalConfig[confirmAction] : null;

  const onClose = () => {
    dispatch(toggleMobileCart(false));
    dispatch(setPropsState({ ...defaultHomePropsState }));
  };

  const handleBackFromPersonalInfo = () => {
    setShowPersonalInfo(false);
  };

  const handleBackFromOrders = () => {
    setShowOrders(false);
  };

  const handleBackFromPaymentMethods = () => {
    setShowPaymentMethods(false);
  };

  const handleBackFromSavedAddresses = () => {
    setShowSavedAddresses(false);
  };

  // If showing personal information, render that component instead
  if (showPersonalInfo) {
    return (
      <PersonalInformation
        currentUser={currentUser}
        breakPoint={breakPoint}
        userDetails={userDetails}
        onBack={handleBackFromPersonalInfo}
        sidebarAnimationClass={sidebarAnimationClass}
      />
    );
  }

  // If showing orders, render that component instead
  if (showOrders) {
    return (
      <Orders
        breakPoint={breakPoint}
        userDetails={userDetails}
        onBack={handleBackFromOrders}
        sidebarAnimationClass={sidebarAnimationClass}
      />
    );
  }

  if (showPaymentMethods) {
    return (
      <PaymentMethods
        userDetails={userDetails}
        onBack={handleBackFromPaymentMethods}
        sidebarAnimationClass={sidebarAnimationClass}
        fetchCreditCardsAdyen={fetchCreditCardsAdyen}
      />
    );
  }

  if (showSavedAddresses) {
    return (
      <SavedAddresses
        breakPoint={breakPoint}
        userDetails={userDetails}
        onBack={handleBackFromSavedAddresses}
      />
    );
  }

  return (
    <AnimationWrapper
      animation='fade-in'
      style={{
        width: '100%',
        height: '100%',
      }}
      className='profile-section'
    >
      <aside className='sidebar'>
        <div className='sidebar__wrapper'>
          <div className='card__about'>
            <BackButtonCWUI
              extendedClassName={`mt-4 ml-5 ${
                breakPoint <= BREAKPOINT_VALUES.DESKTOP ? 'hidden' : ''
              }`}
              onClick={onClose}
            />
            <div
              className={`profile-header ${
                breakPoint <= BREAKPOINT_VALUES.DESKTOP ? 'pt-[50px]' : ''
              }`}
            >
              <CustomerCategoryTitleUI
                extendedClassName={`${
                  breakPoint >= BREAKPOINT_VALUES.DESKTOP ? 'mt-4' : ''
                }`}
              >
                Profile
              </CustomerCategoryTitleUI>
            </div>

            <div className='user-details'>
              <div className='user-name'>
                {userDetails.firstName} {userDetails.lastName}
              </div>
              <div className='flex mt-5 gap-30'>
                <div>
                  <div className='user-details-label'>Member Since</div>
                  <div className='user-details-value'>{formattedDate}</div>
                </div>
                <div>
                  <div className='user-details-label'>Total Orders</div>
                  <div className='user-details-value'>
                    {userDetails.totalOrders}
                  </div>
                </div>
              </div>
            </div>

            <div className='mt-3 max-h-[calc(100vh-300px)] overflow-y-auto pr-2'>
              {profileItems.map(
                ({
                  name,
                  icon: Icon,
                  isNotShowArrow,
                  isDestructive,
                  onClick,
                }) => (
                  <div
                    key={name}
                    className='flex items-center justify-between px-6 py-[22px] cursor-pointer active:bg-gray-200 sm:active:bg-transparent transition-colors duration-100'
                    onClick={onClick}
                  >
                    <div
                      className={`flex items-center gap-4 ${
                        isDestructive
                          ? 'text-red-500 hover:text-red-400'
                          : 'text-gray-700'
                      }`}
                    >
                      <Icon className='w-7 h-7' />
                      <span className='text-lg'>{name}</span>
                    </div>
                    {!isNotShowArrow && (
                      <span className='text-gray-400'>
                        <RightArrowIcon className='w-4 h-4' />
                      </span>
                    )}
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      </aside>
      <ConfirmationModal
        open={!!confirmAction}
        onOpenChange={(isOpen) => {
          if (!isOpen) setConfirmAction(null);
        }}
        onCancel={() => setConfirmAction(null)}
        onConfirm={() => {
          const handler = activeConfirm?.onConfirm;
          setConfirmAction(null);
          handler?.();
        }}
        title={activeConfirm?.title}
        description={activeConfirm?.description}
        confirmText={activeConfirm?.confirmText}
        destructive={activeConfirm?.destructive}
      />
    </AnimationWrapper>
  );
}
